import { CurrencyConstants, IndexedDBCache, TransferConstants } from "finhelper";
import { Api } from "laravel-request";

import FilterAccount from "#app/domain/clients/FilterAccount";

import type { ApiResponse } from "../types/api.types";
import type { ApiRawRecord } from "../types/common";
import type { LaravelQueryBuilder } from "../types/laravelRequest";

import { buildFunnelChartClientsQuery } from "./buildFunnelChartClientsQuery";
import {
    buildFunnelCrmClientServicesQuery,
    extractFunnelScopedClientIdsArray,
} from "./buildFunnelCrmClientServicesQuery";
import type { FunnelStatisticsDataset } from "./types";

type AccountWithSubAccounts = ApiRawRecord & {
    accounts?: Array<ApiRawRecord & { id?: number }>;
};

const laravelAll = <T>(query: { all: (ok: (r: T) => void, err?: (e: any) => void) => void }): Promise<T> =>
    new Promise((resolve, reject) => {
        query.all(
            (response) => resolve(response),
            (error) => reject(error)
        );
    });

const MAIN_STATS_KEY = "main_stats";
const MAIN_STATS_TTL_MS = 1000 * 60 * 60 * 24;

type MainStatsPayload = {
    serviceData: unknown[];
    transferData: unknown[];
    accountData: unknown[];
    clientsData: unknown[];
    withFinanceGroup?: boolean;
};

const wrapMainStatsAsDataset = (cached: MainStatsPayload): FunnelStatisticsDataset =>
    ({
        serviceResponse: { data: cached.serviceData },
        transferResponse: { data: cached.transferData },
        accountResponse: { data: cached.accountData },
        clientsResponse: { data: cached.clientsData },
    } as FunnelStatisticsDataset);

/** Кеш пригоден только если есть клиенты и совпадает режим фингруппы (иначе цифры разойдутся). */
const isUsableMainStats = (cached: unknown, withFinanceGroup: boolean): cached is MainStatsPayload =>
    cached != null &&
    typeof cached === "object" &&
    Array.isArray((cached as MainStatsPayload).clientsData) &&
    (cached as MainStatsPayload).clientsData.length > 0 &&
    ((cached as MainStatsPayload).withFinanceGroup ?? true) === withFinanceGroup;

/**
 * Сетевой батч для вкладки статистики: счета, переводы, клиенты, услуги → IndexedDB main_stats.
 */
const fetchFunnelStatisticsFromNetwork = (
    withFinanceGroup: boolean,
    domainUrl: string | undefined | null
): Promise<FunnelStatisticsDataset> => {
    let apiRequest = Api.get("user-account", "index", { all_users: 1 });
    if (domainUrl) apiRequest = apiRequest.setDomain(domainUrl);

    return laravelAll<ApiResponse<AccountWithSubAccounts[]>>(apiRequest.whereIn("bank_id", FilterAccount.ACCOUNT_BANKS).with("accounts")).then(
        async (accountResponse) => {
            const accountIds: number[] = [];
            (accountResponse.data ?? []).forEach((account) => {
                (account.accounts ?? []).forEach((subAccount) => {
                    if (typeof subAccount.id === "number") {
                        accountIds.push(subAccount.id);
                    }
                });
            });

            let transferApiRequest = Api.get("transfer", "index", {
                all_users: 1,
                currency_id: CurrencyConstants.RUBBLE_ID,
            });

            if (domainUrl) transferApiRequest = transferApiRequest.setDomain(domainUrl);

            const transferResponse = await laravelAll<ApiResponse<unknown[]>>(
                transferApiRequest
                    .where((query: LaravelQueryBuilder) =>
                        query
                            .where((nested: LaravelQueryBuilder) =>
                                nested
                                    .where("comment", "Отозвано со счета")
                                    .orWhere("comment", "Поступило на счёт")
                            )
                            .orWhereHasMorph?.("operation", ["tinkoff.operation"], (operationQuery: LaravelQueryBuilder) =>
                                operationQuery.whereIn("operation_type", [1, 9])
                            ) ?? query
                    )
                    .where((query: LaravelQueryBuilder) =>
                        accountIds.length
                            ? query.whereIn("from_account_id", accountIds).orWhereIn?.("to_account_id", accountIds) ?? query
                            : query
                    )
                    .whereIn("action_type_id", [
                        TransferConstants.BETWEEN_ACCOUNTS,
                        TransferConstants.INCOME,
                        TransferConstants.SPENDING,
                    ])
                    .orderBy("paid_at")
                    .with("operation")
            );

            const clientsResponse = await laravelAll(buildFunnelChartClientsQuery(withFinanceGroup));
            const scopedClientIdsArray = extractFunnelScopedClientIdsArray(clientsResponse.data);

            const serviceResponse =
                scopedClientIdsArray.length === 0
                    ? ({ data: [] } as ApiResponse<unknown[]>)
                    : await laravelAll<ApiResponse<unknown[]>>(
                          buildFunnelCrmClientServicesQuery(withFinanceGroup, scopedClientIdsArray)
                      );

            await IndexedDBCache.set(
                MAIN_STATS_KEY,
                {
                    serviceData: serviceResponse.data,
                    transferData: transferResponse.data,
                    accountData: accountResponse.data,
                    clientsData: clientsResponse.data,
                    withFinanceGroup,
                },
                MAIN_STATS_TTL_MS
            );

            return {
                serviceResponse,
                transferResponse,
                accountResponse,
                clientsResponse,
            } as FunnelStatisticsDataset;
        }
    );
};

/**
 * Stale-while-revalidate: при наличии свежего `main_stats` (TTL 24ч, тот же режим фингруппы)
 * возвращаем кеш мгновенно и обновляем его сетью в фоне. Иначе — обычный сетевой батч.
 */
export const loadFunnelStatisticsDataset = async (
    withFinanceGroup: boolean,
    domainUrl: string | undefined | null = null
): Promise<FunnelStatisticsDataset> => {
    const cached = await IndexedDBCache.get(MAIN_STATS_KEY);
    if (isUsableMainStats(cached, withFinanceGroup)) {
        // Фоновая ревалидация: не блокируем UI, обновляем кеш к следующему входу.
        void fetchFunnelStatisticsFromNetwork(withFinanceGroup, domainUrl).catch(() => undefined);
        return wrapMainStatsAsDataset(cached);
    }
    return fetchFunnelStatisticsFromNetwork(withFinanceGroup, domainUrl);
};
